import React from "react";
import { LinePath, AreaClosed } from "@visx/shape";
import { scaleTime, scaleLinear } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { curveMonotoneX } from "@visx/curve";
import { localPoint } from "@visx/event";
import { useTooltip, TooltipWithBounds, defaultStyles } from "@visx/tooltip";
import { bisector } from "d3-array";

const tooltipStyles = {
  ...defaultStyles,
  background: "rgba(0, 0, 0, 0.85)",
  border: "none",
  color: "white",
  padding: "8px 12px",
};

const bisectDate = bisector((d) => new Date(d.trade_date)).left;

const MarketVolumeChart = ({ width, height, data }) => {
  // Ensure valid dimensions and add left margin for y-axis labels
  const margin = { left: 80, right: 20, top: 20, bottom: 40 };
  const chartWidth = Math.max(width, 300);
  const chartHeight = Math.max(height, 200);

  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip();

  const xScale = React.useMemo(
    () =>
      scaleTime({
        domain: [
          new Date(data[0].trade_date),
          new Date(data[data.length - 1].trade_date),
        ],
        range: [margin.left, chartWidth - margin.right],
      }),
    [data, chartWidth, margin.left, margin.right]
  );

  const yScale = React.useMemo(
    () => {
      const maxValue = Math.max(...data.map((d) => parseFloat(d.total_volume_dollar)));
      
      // Calculate nice round numbers for the domain
      const step = 100000; // 100k steps
      const yMax = Math.ceil(maxValue / step) * step;
      
      return scaleLinear({
        domain: [0, yMax], // Always start from 0
        range: [chartHeight - margin.bottom, margin.top],
        nice: true,
      });
    },
    [data, chartHeight, margin.bottom, margin.top]
  );

  // Format large numbers with K/M/B suffix
  const formatYAxisTick = (value) => {
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return value.toFixed(0);
  };

  // Format date for tooltip
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  // Format date for x-axis
  const formatAxisDate = (date) => {
    const d = new Date(date);
    // For smaller screens or when we have lots of data points, just show the date
    if (width < 768 || data.length > 14) {
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
    // For larger screens with fewer data points, show more detail
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  };

  // Calculate number of ticks based on screen width
  const getXAxisTickCount = () => {
    if (width < 480) return 3; // Mobile
    if (width < 768) return 5; // Tablet
    return 7; // Desktop
  };

  const handleTooltip = React.useCallback(
    (event) => {
      const { x } = localPoint(event) || { x: 0 };
      const x0 = xScale.invert(x);
      const index = bisectDate(data, x0, 1);
      const d0 = data[index - 1];
      const d1 = data[index];
      let d = d0;
      if (d1 && d0) {
        d = x0 - new Date(d0.trade_date) > new Date(d1.trade_date) - x0 ? d1 : d0;
      }

      showTooltip({
        tooltipData: d,
        tooltipLeft: xScale(new Date(d.trade_date)),
        tooltipTop: yScale(d.total_volume_dollar),
      });
    },
    [showTooltip, xScale, yScale, data]
  );

  return (
    <div style={{ position: "relative" }}>
      <svg width={chartWidth} height={chartHeight}>
        <defs>
          <linearGradient id="area-gradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#667EEA" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#667EEA" stopOpacity={0.1} />
          </linearGradient>
        </defs>

        {/* Area fill */}
        <AreaClosed
          data={data}
          x={(d) => xScale(new Date(d.trade_date))}
          y={(d) => yScale(d.total_volume_dollar)}
          yScale={yScale}
          stroke="transparent"
          fill="url(#area-gradient)"
          curve={curveMonotoneX}
        />

        {/* Line Path */}
        <LinePath
          data={data}
          x={(d) => xScale(new Date(d.trade_date))}
          y={(d) => yScale(d.total_volume_dollar)}
          stroke="#667EEA"
          strokeWidth={2}
          curve={curveMonotoneX}
        />

        {/* X Axis */}
        <AxisBottom
          top={chartHeight - margin.bottom}
          scale={xScale}
          stroke="#ccc"
          tickStroke="#ccc"
          numTicks={getXAxisTickCount()}
          tickFormat={formatAxisDate}
          tickLabelProps={() => ({
            fill: "#aaa",
            fontSize: 12,
            textAnchor: "middle",
            transform: "translate(0, 8)", // Move labels down slightly
          })}
          label="Date"
          labelOffset={32}
          labelProps={{ 
            fill: "#aaa", 
            fontSize: 12, 
            textAnchor: "middle",
          }}
        />

        {/* Y Axis (No Title) */}
        <AxisLeft
          left={margin.left}
          scale={yScale}
          stroke="#ccc"
          tickStroke="#ccc"
          tickFormat={formatYAxisTick}
          numTicks={8} // Control number of ticks for consistent spacing
          tickLabelProps={() => ({
            fill: "#aaa",
            fontSize: 12,
            textAnchor: "end",
            dy: 4,
            dx: -8,
          })}
        />

        {/* Invisible rect for mouse tracking */}
        <rect
          x={margin.left}
          y={margin.top}
          width={chartWidth - margin.left - margin.right}
          height={chartHeight - margin.top - margin.bottom}
          fill="transparent"
          onMouseMove={handleTooltip}
          onMouseLeave={() => hideTooltip()}
        />

        {/* Tooltip vertical line and dot */}
        {tooltipData && (
          <>
            <line
              x1={tooltipLeft}
              x2={tooltipLeft}
              y1={margin.top}
              y2={chartHeight - margin.bottom}
              stroke="#667EEA"
              strokeWidth={1}
              strokeDasharray="4,4"
              pointerEvents="none"
            />
            <circle
              cx={tooltipLeft}
              cy={tooltipTop}
              r={4}
              fill="#667EEA"
              stroke="white"
              strokeWidth={2}
              pointerEvents="none"
            />
          </>
        )}
      </svg>

      {/* Tooltip */}
      {tooltipData && (
        <TooltipWithBounds
          key={Math.random()} // force remount
          top={tooltipTop - 12}
          left={tooltipLeft + 12}
          style={tooltipStyles}
        >
          <div className="text-sm">
            <div className="font-medium">{formatDate(tooltipData.trade_date)}</div>
            <div className="mt-1 text-[#667EEA]">
              Volume: ${formatYAxisTick(tooltipData.total_volume_dollar)}
            </div>
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};

export default MarketVolumeChart;